/**
 * Selectors for fuel-saving activity.
 * Additional app-level selectors can be added here as needed.
 */
import type { StateTree } from '@makemydeal/dr-dash-types';
import {
    getAsyncScenarioExpectedIds,
    getAsyncScenarioMissingIds
} from '@makemydeal/dr-dash-store/selectors/asyncScenarioLoadSelectors';
import { getActiveComparison } from '@makemydeal/dr-dash-store/selectors/multiScenarioSelectors';

/**
 * Returns true while non-primary scenario vehicles are still being loaded.
 * Covers three phases:
 *  1. Comparison has non-primary scenarios but async expected IDs haven't been
 *     registered yet (early timing window on page load).
 *  2. Expected IDs are registered and some are still missing (fetch in flight).
 *  3. Fully loaded → false.
 */
export const isScenariosLoading = (state: StateTree): boolean => {
    // Stop loading once fuel-saving scenario processing is settled.
    // Host async tracking can remain "missing" for scenarios that share the primary VIN.
    if (state.fuelSaving?.isScenarioLoadSettled) {
        return false;
    }

    const expected = getAsyncScenarioExpectedIds(state);

    // Phase 1: expected IDs not yet registered — use comparison as an early signal.
    if (expected.length === 0) {
        const nonPrimaryScenarios = getActiveComparison(state)?.scenarios?.filter((s) => !s.isPrimary) ?? [];
        return nonPrimaryScenarios.length > 0;
    }

    // Phase 2: expected IDs registered, waiting for scoped state hydration.
    return getAsyncScenarioMissingIds(state).length > 0;
};
